<script>
  import { onMount } from 'svelte'
  import { settings, overlay, selectedCurrencyCode, liveFxRates, flipToggle } from '../stores.js'
  import Sidebar from '../components/Sidebar.svelte'
  import AboutOverlay from '../components/AboutOverlay.svelte'
  import DonationOverlay from '../components/DonationOverlay.svelte'
  import Alerts from '../components/alert/Alerts.svelte'
  import SearchBar from '../components/SearchBar.svelte'
  import { integerFormat } from '../utils/format.js'
  import config from '../config.js'
  import Icon from '../components/Icon.svelte'
  import { tweened } from 'svelte/motion'
  import { cubicInOut } from 'svelte/easing'
  import { currencies } from '../data/coins.js'
  import { calcRate, calcNumFiatCoins, formatCurrency } from '../utils/currency.js'
  import { getFxRates } from '../api/api.js'

  import Carousel from '../components/Carousel.svelte'
  import CoinComparison from '../components/CoinComparison.svelte'

  window.sr = (rate) => {
    $liveFxRates[$selectedCurrencyCode] = rate
  }
  window.currencies = currencies

  onMount(() => {
    // if (!$settings.showDonation) $settings.showDonation = true
  })

  init()

  if (config.dev) console.log(`Loaded ${Object.keys(currencies).length} currencies`)

  async function init () {
    await getFxRates()
    updateCurrencies()
    onSelectByCode($selectedCurrencyCode)
  }

  function onSelectByCode (newCurrency, lastCurrencyCode) {
    history.replaceState(null, newCurrency, `/${newCurrency}`)
    updateCurrencies(lastCurrencyCode)
  }

  $: {
    onSelectByCode($selectedCurrencyCode)
  }

  $: {
    if (selectedCurrency != null && $liveFxRates) {
      isFiatBase = calcNumFiatCoins(selectedCurrency, $liveFxRates) <= 1
    }
  }

  let sortingMode = 'numCoins'
  let sortedCurrencies = Object.values(currencies)
  let selectedCurrency = currencies.USD
  let selectedCurrencyRate = 1
  let tweenedRate = tweened(0.0, { duration: 1500, easing: cubicInOut })
  let isFiatBase

  let preloadPrevImg = new Image()
  let preloadNextImg = new Image()

  $: {
    sortedCurrencies = Object.values(currencies).sort(sortingFunctions[sortingMode])
  }

  $: {
    if (!isNaN(selectedCurrencyRate)) $tweenedRate = selectedCurrencyRate
  }

  $: {
    if ($liveFxRates && selectedCurrency) {
      onRateChange()
    } else selectedCurrencyRate = 1
  }

  function onRateChange() {
    selectedCurrencyRate = calcRate(selectedCurrency, $liveFxRates)
    updateCurrencies($selectedCurrencyCode)
  }

  function updateCurrencies () {
    window.rates = $liveFxRates
    selectedCurrency = currencies[$selectedCurrencyCode || 'USD']

    sortedCurrencies = Object.values(currencies).sort(sortingFunctions[sortingMode])

    if ($liveFxRates) {
      selectedCurrencyRate = calcRate(selectedCurrency, $liveFxRates)
    } else selectedCurrencyRate = 1

    const selectedCurrencyIndex = sortedCurrencies.findIndex(currency => {
      return currency.code === $selectedCurrencyCode
    })
    const prevSelectedCurrencyIndex = selectedCurrencyIndex ? selectedCurrencyIndex - 1 : sortedCurrencies.length - 1
    const nextSelectedCurrencyIndex = (selectedCurrencyIndex === sortedCurrencies.length - 1) ? 0 : selectedCurrencyIndex + 1
    const prevSelectedCurrency = currencies[sortedCurrencies[prevSelectedCurrencyIndex]]
    const nextSelectedCurrency = currencies[sortedCurrencies[nextSelectedCurrencyIndex]]
    preloadPrevImg.src = sortedCurrencies[prevSelectedCurrencyIndex].coins[0].img
    preloadNextImg.src = sortedCurrencies[nextSelectedCurrencyIndex].coins[0].img
  }

  function nextCurrency () {
    scrollCurrency(1)
  }

  function prevCurrency () {
    scrollCurrency(-1)
  }

  function scrollCurrency (offset) {
    let currentIndex = sortedCurrencies.findIndex(currency => {
      return currency.code === $selectedCurrencyCode
    })
    const targetIndex = currentIndex + offset
    if (targetIndex < 0) targetIndex = sortedCurrencies.length - 1
    else if (targetIndex >= sortedCurrencies.length - 1) targetIndex = 0
    $selectedCurrencyCode = sortedCurrencies[targetIndex].code
  }

  export const sortingFunctions = {
    // Sort by number of coins per sat (ascending)
    numCoins: (a, b) => {
      const numA = calcNumFiatCoins(a, $liveFxRates)
      const numB = calcNumFiatCoins(b, $liveFxRates)
      if (numA === numB) {
        return (b.fxCode ? 1 : 0) - (a.fxCode ? 1 : 0)
      } else return numB - numA
    },
    // Sort alphabetically by currency code
    currencyCode: (a, b) => {
      return a.code < b.code
    }
  }

  function flipCoins () {
    $flipToggle = !$flipToggle
  }
</script>

<style type="text/scss">
  .main {
    width: 100%;
    min-height: 100vh;
    background: var(--palette-a);
    overflow: hidden;

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .carousel-wrapper {
    width: 1360px;
    max-width: 90%;
    margin-top: 80px;
    max-height: calc(100vh - 100px);
  }

  .fx-rate {
    font-size: 1.4rem;
    line-height: 2.4em;
    color: var(--palette-bold-a);
  }

  .not-mobile {
    display: block;
  }

  .top-bar {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
  }

  @media screen and (max-width: 900px), screen and (max-height: 800px) {
    .not-mobile {
      display: none;
    }
  }
</style>

<div class="main" class:light-mode={!$settings.darkMode}>

  <div class="not-mobile">
    <SearchBar color={$settings.darkMode ? 'white' : 'var(--palette-bold-a)'} />
  </div>
  <div class="carousel-wrapper">
    <Carousel on:next={nextCurrency} on:prev={prevCurrency} >
      <CoinComparison currency={selectedCurrency} flipped={isFiatBase != $flipToggle} on:flip={flipCoins} />
    </Carousel>
  </div>
  <p class="fx-rate">
    ₿ 1 &thickapprox; { formatCurrency(selectedCurrency, $tweenedRate) }
  </p>

  <div class="top-bar">
    <div class="spacer" />
    {#if config.messagesEnabled && $settings.showMessages }
      <Alerts />
    {/if}
  </div>

  <Sidebar />

  <AboutOverlay />
  {#if config.donationsEnabled }
    <DonationOverlay />
  {/if}
</div>
